/**
 * Firebase Storage Download URL Utilities
 *
 * Unified functions for generating storage download URLs.
 * Bundles similar functionality for audio, images, and PDFs.
 *
 * @module lib/storage/download-urls
 */

import { buildStorageUrl } from "@/lib/storage-url";
import { getFirebaseStorage } from "@/lib/firebase";
import { ref, getDownloadURL } from "firebase/storage";

// =============================================================================
// TYPES
// =============================================================================

/**
 * Supported storage file types
 */
export type StorageFileType = "audio" | "images" | "pdfs";

/**
 * Storage path context required for URL generation
 */
export interface StorageContext {
  userId: string;
  projectId: string;
}

// =============================================================================
// UNIFIED URL GENERATORS
// =============================================================================

/**
 * Generate Firebase Storage download URL for any file type.
 * Returns basic URL without authentication token.
 * For authenticated URLs with tokens, use getAuthenticatedStorageUrl().
 *
 * @param fileType - Type of file (audio, images, pdfs)
 * @param fileName - File name (e.g., "1734002024528_audio.webm")
 * @param context - Storage context with userId and projectId
 * @returns Firebase Storage download URL (without token)
 *
 * @example
 * ```ts
 * // Basic URL (will fail if Firebase Storage rules require auth)
 * const url = generateStorageUrl("images", "photo.jpg", { userId, projectId });
 * ```
 */
export function generateStorageUrl(
  fileType: StorageFileType,
  fileName: string,
  context: StorageContext
): string {
  return buildStorageUrl(context.userId, context.projectId, fileType, fileName);
}

/**
 * Get authenticated Firebase Storage download URL for any file type.
 * Single source of truth for authenticated storage URLs.
 * Uses Firebase SDK's getDownloadURL which generates a signed URL with auth token.
 * Required when Firebase Storage security rules require authentication.
 *
 * @param fileType - Type of file (audio, images, pdfs)
 * @param fileName - File name
 * @param context - Storage context with userId and projectId
 * @returns Promise of Firebase Storage download URL with auth token
 *
 * @example
 * ```ts
 * // Authenticated URL with token (for private user files)
 * const url = await getAuthenticatedStorageUrl("images", "photo.jpg", { userId, projectId });
 * ```
 */
export async function getAuthenticatedStorageUrl(
  fileType: StorageFileType,
  fileName: string,
  context: StorageContext
): Promise<string> {
  const storage = getFirebaseStorage();
  const path = `users/${context.userId}/projects/${context.projectId}/${fileType}/${fileName}`;
  const storageRef = ref(storage, path);
  return getDownloadURL(storageRef);
}

